/* 1a — the card column on the right of the desktop Watercraft banner.
   A scaled-down still of the Watercraft app's card library, ported from
   `01-placements/1a_homepage-banner-desktop-web.html` in the handoff pack.
   Purely decorative: no links, no live data, hidden from assistive tech and
   from the layout entirely below the `wc-1a` desktop breakpoint.

   Structure is the pack's; every colour is remapped onto the app palette in
   `tokens.js`, written here as `wcColors` literals because the markup is
   generated and too dense to thread the module through. Fills that sit on a
   card use `surfaceRaised`, dividers use the app's `wcHairline`, and the
   calendar chips are token tints the way the app tints — a status colour
   over the ground at low alpha rather than a separate solid shade.

   The two columns scroll downwards on a loop. Each renders its cards twice
   and shifts by exactly one copy plus one gap, so the seam never shows; the
   shift and duration are measured per column at runtime so both move at the
   same speed despite their different heights. The clipped cards at the top
   and bottom fade out into the ground, as the pack has them. */
import React, { useEffect, useRef } from 'react';

/* Card gap, and how fast the columns drift, in px per second. Both columns
   are driven from this one number, so the speed stays matched between them. */
const GAP = 9;
const SPEED = 26;

function ColumnA() {
  return (
    <>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Conditions · Tue 18
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: '8px',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '13px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              24<span style={{ fontSize: '8px', color: '#8AAB8A' }}>°</span>
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '6.5px',
                letterSpacing: '.1em',
                color: '#6E8270',
                marginTop: '4px',
              }}
            >
              AIR
            </div>
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '13px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              1011
              <span style={{ fontSize: '8px', color: '#3B9EBF' }}>↓</span>
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '6.5px',
                letterSpacing: '.1em',
                color: '#6E8270',
                marginTop: '4px',
              }}
            >
              PRES
            </div>
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '13px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              28
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '6.5px',
                letterSpacing: '.1em',
                color: '#6E8270',
                marginTop: '4px',
              }}
            >
              WIND
            </div>
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '13px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              32<span style={{ fontSize: '8px', color: '#8AAB8A' }}>%</span>
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '6.5px',
                letterSpacing: '.1em',
                color: '#6E8270',
                marginTop: '4px',
              }}
            >
              MOON
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Lunar · Waxing crescent
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
          <svg
            viewBox='0 0 60 60'
            style={{
              width: '38px',
              height: '38px',
              flex: 'none',
              display: 'block',
            }}
          >
            <circle cx='30' cy='30' r='23' fill='#EEF4EB' />
            <ellipse cx='23' cy='30' rx='15' ry='23' fill='#162419' />
            <circle
              cx='30'
              cy='30'
              r='23'
              fill='none'
              stroke='#2A4030'
              strokeWidth='1'
            />
          </svg>
          <div style={{ flex: '1' }}>
            <div
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '12px',
                fontWeight: '700',
                color: '#EEF4EB',
              }}
            >
              Waxing Crescent
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '7px',
                letterSpacing: '.1em',
                color: '#8AAB8A',
                marginTop: '3px',
              }}
            >
              30% ILLUMINATED
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '7px',
                letterSpacing: '.1em',
                color: '#6E8270',
                marginTop: '4px',
              }}
            >
              RISE 10:11 · SET 22:41
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Peak window
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '12.5px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          45% of catches between 07:00 and 10:00.
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '1.5px',
            height: '28px',
            margin: '9px 0 3px',
          }}
        >
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '29px',
              background: '#3DFF6A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '11px',
              background: '#1A5C2A',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: '1',
              height: '2px',
              background: '#1E3024',
              borderRadius: '1.5px',
              display: 'block',
            }}
          ></span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '6.5px',
            color: '#4D7052',
          }}
        >
          <span>00</span>
          <span>12</span>
          <span>24</span>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Your stories
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '13px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          A session on the bank
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '7px',
            letterSpacing: '.14em',
            color: '#3DFF6A',
            marginTop: '5px',
          }}
        >
          ▶ 13 AUG–14 AUG · TAP TO REPLAY
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '7px',
          }}
        >
          <span
            style={{
              fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
              fontSize: '11px',
              fontWeight: '700',
              color: '#EEF4EB',
              whiteSpace: 'nowrap',
            }}
          >
            August 2026
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6.5px',
              letterSpacing: '.1em',
              color: '#6E8270',
              whiteSpace: 'nowrap',
            }}
          >
            WK 31–36
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,1fr)',
            gap: '2px',
            marginBottom: '3px',
          }}
        >
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            M
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            T
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            W
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            T
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            F
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            S
          </span>
          <span
            style={{
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '6px',
              color: '#6E8270',
            }}
          >
            S
          </span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,1fr)',
            gap: '2px',
          }}
        >
          <span style={{ display: 'block' }}></span>
          <span style={{ display: 'block' }}></span>
          <span style={{ display: 'block' }}></span>
          <span style={{ display: 'block' }}></span>
          <span style={{ display: 'block' }}></span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              1
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              25/14°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              2
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              27/15°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              3
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              32/17°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              4
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              30/18°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              5
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              25/18°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              6
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              22/12°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              7
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              25/12°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              8
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              28/12°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              9
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              31/14°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              10
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              26/17°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              11
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              26/15°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              12
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              33/16°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              13
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              36/18°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: '#1E3024',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              14
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EEF4EB',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              33/18°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              15
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              27/20°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              16
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              26/17°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              17
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              28/18°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
              boxShadow: 'inset 0 0 0 1.5px #3DFF6A',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#3DFF6A',
                lineHeight: '1',
              }}
            >
              18
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              27/19°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              19
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              24/16°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              20
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              22/13°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(245,166,35,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              21
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#F5A623',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              18/14°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              22
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              22/11°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              23
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              21/12°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              24
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              22/11°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: '#1E3024',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              25
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EEF4EB',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              24/14°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(61,255,106,.12)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              26
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#3DFF6A',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              18/13°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              27
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              19/11°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              28
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              23/15°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              29
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              20/13°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              30
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              17/12°
            </span>
          </span>
          <span
            style={{
              display: 'block',
              background: 'rgba(239,68,68,.14)',
              borderRadius: '3px',
              padding: '2px 0',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'block',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '7.5px',
                fontWeight: '700',
                color: '#EEF4EB',
                lineHeight: '1',
              }}
            >
              31
            </span>
            <span
              style={{
                display: 'block',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '5px',
                color: '#EF4444',
                lineHeight: '1',
                marginTop: '1px',
              }}
            >
              18/11°
            </span>
          </span>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          My waters
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <span
              style={{
                flex: 'none',
                width: '30px',
                height: '23px',
                borderRadius: '5px',
                background: '#184322',
                position: 'relative',
                overflow: 'hidden',
                display: 'block',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: '3px',
                  borderRadius: '20px 12px 22px 9px',
                  background: '#0B2D4A',
                  border: '1px solid #E6D9BD',
                  display: 'block',
                }}
              ></span>
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Ravenmere
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '10px',
                color: '#3DFF6A',
              }}
            >
              41:08
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <span
              style={{
                flex: 'none',
                width: '30px',
                height: '23px',
                borderRadius: '5px',
                background: '#184322',
                position: 'relative',
                overflow: 'hidden',
                display: 'block',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: '3px',
                  borderRadius: '14px 20px 10px 18px',
                  background: '#0B2D4A',
                  border: '1px solid #E6D9BD',
                  display: 'block',
                }}
              ></span>
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              A12 Lakes
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '10px',
                color: '#8AAB8A',
              }}
            >
              27:04
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <span
              style={{
                flex: 'none',
                width: '30px',
                height: '23px',
                borderRadius: '5px',
                background: '#184322',
                position: 'relative',
                overflow: 'hidden',
                display: 'block',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: '3px',
                  borderRadius: '18px 10px 20px 14px',
                  background: '#0B2D4A',
                  border: '1px solid #E6D9BD',
                  display: 'block',
                }}
              ></span>
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Churchgate
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '10px',
                color: '#8AAB8A',
              }}
            >
              22:12
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

function ColumnB() {
  return (
    <>
      <div
        style={{
          background: '#1E3024',
          border: '1px solid #2E8C47',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#3DFF6A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          ✦ Defining pattern
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '14px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          Too early to call
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '10.5px',
            lineHeight: '1.42',
            color: '#9AB49B',
            fontWeight: '300',
            marginTop: '5px',
          }}
        >
          Every session you've logged has been hot (&gt;20C).
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Species
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '12.5px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          Mirror Carp is 36% of the catch.
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            marginTop: '9px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                flex: 'none',
                width: '64px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8px',
                color: '#8AAB8A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Mirror Carp
            </span>
            <span
              style={{
                flex: '1',
                height: '4px',
                borderRadius: '4px',
                background: '#1E3024',
                display: 'block',
              }}
            ></span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '9.5px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              4
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                flex: 'none',
                width: '64px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8px',
                color: '#8AAB8A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Ghost Carp
            </span>
            <span
              style={{
                flex: '1',
                height: '4px',
                borderRadius: '4px',
                background: '#1E3024',
                display: 'block',
              }}
            ></span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '9.5px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              2
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                flex: 'none',
                width: '64px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8px',
                color: '#8AAB8A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Common Carp
            </span>
            <span
              style={{
                flex: '1',
                height: '4px',
                borderRadius: '4px',
                background: '#1E3024',
                display: 'block',
              }}
            ></span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '9.5px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              1
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                flex: 'none',
                width: '64px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '8px',
                color: '#8AAB8A',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Bream
            </span>
            <span
              style={{
                flex: '1',
                height: '4px',
                borderRadius: '4px',
                background: '#1E3024',
                display: 'block',
              }}
            ></span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '9.5px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              1
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Solunar activity · GOOD
        </div>
        <svg
          viewBox='0 0 240 66'
          style={{ display: 'block', width: '100%', height: '42px' }}
        >
          <g stroke='#2A4030' strokeWidth='1' strokeDasharray='2 3'>
            <line x1='0' y1='10' x2='240' y2='10' />
            <line x1='0' y1='30' x2='240' y2='30' />
            <line x1='0' y1='50' x2='240' y2='50' />
          </g>
          <path
            d='M2 60 C 14 60, 18 26, 30 26 C 42 26, 46 58, 58 58 C 76 58, 82 48, 94 44 C 104 41, 110 48, 120 56 C 132 60, 138 26, 152 26 C 166 26, 170 54, 182 54 C 194 54, 198 42, 210 42 C 224 42, 230 56, 238 58'
            fill='none'
            stroke='#3DFF6A'
            strokeWidth='2'
            strokeLinecap='round'
          />
          <path
            d='M2 62 C 20 62, 26 40, 40 40 C 56 40, 62 62, 84 62 C 120 62, 132 60, 146 60 C 160 60, 166 38, 180 38 C 196 38, 202 62, 238 62'
            fill='none'
            stroke='rgba(230,217,189,.78)'
            strokeWidth='1.3'
            strokeDasharray='3 3'
          />
          <line
            x1='120'
            y1='4'
            x2='120'
            y2='62'
            stroke='#F5A623'
            strokeWidth='1.2'
            strokeDasharray='3 3'
          />
        </svg>
        <div style={{ display: 'flex', gap: '5px', marginTop: '7px' }}>
          <span
            style={{
              flex: '1',
              border: '1px solid #2E8C47',
              borderRadius: '6px',
              padding: '4px 0',
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '8.5px',
              color: '#3DFF6A',
            }}
          >
            05:14
          </span>
          <span
            style={{
              flex: '1',
              border: '1px solid #2A4030',
              borderRadius: '6px',
              padding: '4px 0',
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '8.5px',
              color: '#D6E2D2',
            }}
          >
            13:10
          </span>
          <span
            style={{
              flex: '1',
              border: '1px solid #2E8C47',
              borderRadius: '6px',
              padding: '4px 0',
              textAlign: 'center',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '8.5px',
              color: '#3DFF6A',
            }}
          >
            17:39
          </span>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Learn
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '14px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          Watercraft 101
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '10.5px',
            lineHeight: '1.42',
            color: '#9AB49B',
            fontWeight: '300',
            marginTop: '4px',
          }}
        >
          33 lessons with Scott Lloyd.
        </div>
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          <span
            style={{
              flex: '1',
              borderRadius: '5px',
              background: '#1E3024',
              border: '1px solid #2A4030',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 5px',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '8.5px',
                fontWeight: '600',
                color: '#D6E2D2',
                lineHeight: '1.25',
              }}
            >
              What is Watercraft?
            </span>
          </span>
          <span
            style={{
              flex: '1',
              borderRadius: '5px',
              background: '#1E3024',
              border: '1px solid #2A4030',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px 5px',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '8.5px',
                fontWeight: '600',
                color: '#D6E2D2',
                lineHeight: '1.25',
              }}
            >
              First things first…
            </span>
          </span>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Best swim
        </div>
        <div
          style={{
            fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
            fontSize: '12.5px',
            lineHeight: '1.28',
            fontWeight: '700',
            color: '#EEF4EB',
            letterSpacing: '-.01em',
          }}
        >
          Every session logged is from Swim 1.
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '9px',
          }}
        >
          <span
            style={{
              flex: 'none',
              width: '42px',
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: '8px',
              color: '#8AAB8A',
            }}
          >
            Swim 1
          </span>
          <span
            style={{
              flex: '1',
              height: '4px',
              borderRadius: '4px',
              background: '#1E3024',
              display: 'block',
            }}
          ></span>
          <span
            style={{
              flex: 'none',
              fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
              fontSize: '9.5px',
              fontWeight: '600',
              color: '#EEF4EB',
            }}
          >
            0
          </span>
        </div>
      </div>
      <div
        style={{
          background: '#162419',
          border: '1px solid #2A4030',
          borderRadius: '12px',
          padding: '10px 11px',
          boxShadow: '0 18px 36px rgba(0,0,0,.5)',
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: '8px',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
            color: '#8AAB8A',
            marginBottom: '7px',
            whiteSpace: 'nowrap',
          }}
        >
          Personal bests
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '6px 0',
              borderBottom: '1px solid rgba(238,244,235,.06)',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: '14px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '9px',
                color: '#4D7052',
              }}
            >
              01
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              Common carp
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '11px',
                color: '#3DFF6A',
              }}
            >
              41:08
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '6px 0',
              borderBottom: '1px solid rgba(238,244,235,.06)',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: '14px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '9px',
                color: '#4D7052',
              }}
            >
              02
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              Mirror carp
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '11px',
                color: '#EEF4EB',
              }}
            >
              34:02
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '6px 0',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: '14px',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '9px',
                color: '#4D7052',
              }}
            >
              03
            </span>
            <span
              style={{
                flex: '1',
                fontFamily: "'Libre Franklin',Helvetica,Arial,sans-serif",
                fontSize: '11px',
                fontWeight: '600',
                color: '#EEF4EB',
              }}
            >
              Mirror carp
            </span>
            <span
              style={{
                flex: 'none',
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: '11px',
                color: '#EEF4EB',
              }}
            >
              27:04
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/* One column: its cards twice over, translated up by a whole copy and
   animated back to zero so the motion reads as a continuous downward drift. */
function LoopColumn({ offset, children }) {
  const loopRef = useRef(null);

  useEffect(() => {
    const el = loopRef.current;
    const copy = el && el.firstElementChild;
    if (!copy) return undefined;

    const measure = () => {
      const shift = copy.getBoundingClientRect().height + GAP;
      el.style.setProperty('--wc-loop-shift', shift + 'px');
      el.style.animationDuration = shift / SPEED + 's';
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(copy);
    return () => ro.disconnect();
  }, []);

  const stack = { display: 'flex', flexDirection: 'column', gap: GAP + 'px' };
  return (
    <div style={{ ...stack, transform: 'translateY(' + offset + ')' }}>
      <div ref={loopRef} className='wc-1a__loop' style={stack}>
        <div style={stack}>{children}</div>
        <div style={stack}>{children}</div>
      </div>
    </div>
  );
}

export default function WatercraftCardWall() {
  return (
    <div
      className='wc-1a__wall'
      aria-hidden='true'
      style={{
        position: 'relative',
        /* Its own ground, as the design has it (#0A1410 there). This is what
           keeps the left column's glow off the cards.

           The design also lays a green radial over this column, but there it
           sits almost entirely behind the cards and contributes nothing you
           can see — the exposed ground measures #0A1C11 against a banner
           ground of #0B180E. Full-bleed puts a wide flat ground beside this
           column, which made that falloff read as the glow spilling out, so
           the radial is not carried over. */
        background: '#0C1A0F',
        overflow: 'hidden',
        minHeight: '486px',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '20px',
          right: '14px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '14px',
          alignItems: 'start',
        }}
      >
        <LoopColumn offset='-24px'>
          <ColumnA />
        </LoopColumn>
        <LoopColumn offset='-30px'>
          <ColumnB />
        </LoopColumn>
      </div>
      <div
        style={{
          position: 'absolute',
          left: '0',
          right: '0',
          top: '0',
          height: '46px',
          background:
            'linear-gradient(180deg,#0C1A0F 0%,rgba(12,26,15,0) 100%)',
          pointerEvents: 'none',
        }}
      ></div>
      <div
        style={{
          position: 'absolute',
          left: '0',
          right: '0',
          bottom: '0',
          height: '76px',
          background: 'linear-gradient(0deg,#0C1A0F 0%,rgba(12,26,15,0) 100%)',
          pointerEvents: 'none',
        }}
      ></div>
    </div>
  );
}
